import { ApolloProvider } from '@apollo/client'
import CssBaseline from '@mui/material/CssBaseline'
import { LocalizationProvider } from '@mui/x-date-pickers-pro'
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Suspense, useEffect } from 'react'
import { Toaster } from 'react-hot-toast'
import { Outlet, useLocation, useNavigate, useSearchParams } from 'react-router'
import { TooltipProvider } from '~/@/components/ui/tooltip'
import { apolloClient } from '~/common/apollo-client'
import { WorkOSAuthProvider } from '~/common/contexts/WorkOSAuthContext'
import { ActivityFeedProvider } from '~/common/hooks/useActivityFeed'
import { ApplicationOrientationProvider } from '~/common/hooks/useApplicationOrientation'
import { BreadcrumbsProvider } from '~/common/hooks/useBreadcrumbs'
import { useDeviceType } from '~/common/hooks/useDeviceQuery'
import { DossSignalsProvider } from '~/common/hooks/useDossSignals'
import { EmbedTitleProvider } from '~/common/hooks/useEmbedTitle'
import { GlobalContextProvider, useGlobalContext } from '~/common/hooks/useGlobalContext'
import { GlobalThemeProvider } from '~/common/hooks/useGlobalTheme'
import { NotificationsProvider } from '~/common/hooks/useNotifications'
import { OrgProjectsProvider } from '~/common/hooks/useOrgProjects'
import { PageProvider } from '~/common/hooks/usePage'
import { TableLoadingProvider } from '~/common/hooks/useTableLoadingContext'
import { PerfTelemetryTracker } from '~/common/telemetry/PerfTelemetryTracker'
import { AuthGuard } from '~/components/AuthGuard'
import { IdentifyUser } from '~/components/IdentifyUser'
import { Layout } from '~/components/Layout'
import { LoadingScreen } from '~/components/LoadingScreen'
import { MaintenanceModeGuard } from '~/components/MaintenanceModeGuard'
import { DossSnackbarProvider } from '~/components/shared/DossSnackbar'
import { TermsAndConditionsDialog } from '~/components/TermsAndConditionsDialog'
import { pageJitsu } from '~/jitsu'

const queryClient = new QueryClient()

const JitsuPageTracker = () => {
  const { pathname } = useLocation()

  // Track every route change as a page view in Jitsu
  useEffect(() => {
    pageJitsu()
  }, [pathname])

  return null
}

/** Root layout — global providers that wrap ALL routes. */
export const RootLayout = () => (
  <ApolloProvider client={apolloClient}>
    <WorkOSAuthProvider>
      <ApplicationOrientationProvider>
        <GlobalThemeProvider>
          <TooltipProvider>
            <CssBaseline />
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <MaintenanceModeGuard>
                <QueryClientProvider client={queryClient}>
                  <GlobalContextProvider>
                    <JitsuPageTracker />
                    <PerfTelemetryTracker />
                    <Outlet />
                  </GlobalContextProvider>
                  <Toaster position="bottom-right" />
                </QueryClientProvider>
              </MaintenanceModeGuard>
            </LocalizationProvider>
          </TooltipProvider>
        </GlobalThemeProvider>
      </ApplicationOrientationProvider>
    </WorkOSAuthProvider>
  </ApolloProvider>
)

/** Layout for public routes. Redirects authenticated users away from auth pages. */
export const PublicLayout = () => {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user, isLoading } = useGlobalContext()
  const { isMobile } = useDeviceType()

  useEffect(() => {
    if (user && pathname.startsWith('/auth/')) {
      navigate(searchParams.get('redirect') ?? (isMobile ? '/forms' : '/'), { replace: true })
    }
  }, [user, pathname, navigate, searchParams, isMobile])

  if (isLoading && pathname.startsWith('/auth/')) {
    return <LoadingScreen />
  }
  return (
    <Suspense fallback={<LoadingScreen />}>
      <Outlet />
    </Suspense>
  )
}

/** Layout for authenticated routes — full app shell with sidebar, activity feed, etc. */
export const AppLayout = () => (
  <NotificationsProvider>
    <AuthGuard>
      <TableLoadingProvider>
        <DossSnackbarProvider>
          <OrgProjectsProvider>
            <PageProvider>
              <BreadcrumbsProvider>
                <DossSignalsProvider>
                  <ActivityFeedProvider>
                    <TermsAndConditionsDialog />
                    <IdentifyUser />
                    <EmbedTitleProvider>
                      <Layout>
                        <Suspense fallback={<LoadingScreen />}>
                          <Outlet />
                        </Suspense>
                      </Layout>
                    </EmbedTitleProvider>
                  </ActivityFeedProvider>
                </DossSignalsProvider>
              </BreadcrumbsProvider>
            </PageProvider>
          </OrgProjectsProvider>
        </DossSnackbarProvider>
      </TableLoadingProvider>
    </AuthGuard>
  </NotificationsProvider>
)

/** Guard layout for admin-only routes. */
export const AdminGuardLayout = () => {
  const navigate = useNavigate()
  const { isAdmin, isInternal, isLoading } = useGlobalContext()

  useEffect(() => {
    if (!isLoading && !isAdmin && !isInternal) {
      navigate('/404', { replace: true })
    }
  }, [isLoading, isAdmin, isInternal, navigate])

  if (isLoading || (!isAdmin && !isInternal)) {
    return null
  }
  return <Outlet />
}
